import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Combobox from "@/components/Combobox";
import DatePicker from "@/components/DatePicker";
import { shortDate } from "@/lib/format";
import { CalendarDays, Ship, Search, Anchor, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const CARRIER_COLORS = {
  MAERSK: "#42B0D5", MSC: "#053D7C", CMA: "#0066B2", "CMA CGM": "#0066B2",
  ONE: "#FF1E1E", COSCO: "#D7141A", EVERGREEN: "#0E8E3E", HAPAG: "#FF6900",
  OOCL: "#005EB8", ZIM: "#FF6600", PIL: "#1F4E79", ANL: "#003E7E",
  YML: "#0086C7", HMM: "#003A70", "TS LINES": "#E30613", TSLINES: "#E30613",
};

export default function SailingSchedules() {
  const [pols, setPols] = useState([]);
  const [pods, setPods] = useState([]);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    api.get("/cargoptic-pricing/ports", { params: { direction: "SB", role: "pol" } }).then((r) => setPols(r.data));
  }, []);

  useEffect(() => {
    if (!origin) { setPods([]); return; }
    api.get("/cargoptic-pricing/lanes", { params: { direction: "SB", pol: origin } }).then((r) => setPods(r.data));
  }, [origin]);

  // Clear stale results when the user changes filters.
  useEffect(() => { setRows([]); setHasSearched(false); }, [origin, destination, from, to]);

  const run = async () => {
    setBusy(true);
    try {
      const params = { limit: 100 };
      if (origin) params.origin = origin;
      if (destination) params.destination = destination;
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const r = await api.get("/schedules", { params });
      setRows(r.data);
      setHasSearched(true);
    } catch (e) {
      toast.error("Schedule lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setOrigin(""); setDestination(""); setFrom(""); setTo(""); setRows([]); setHasSearched(false); };

  // Group by ETD week for a calendar-like layout
  const weeks = useMemo(() => {
    const out = new Map();
    rows.forEach((r) => {
      const etd = new Date(r.etd);
      const key = `${etd.getUTCFullYear()}-W${String(getISOWeek(etd)).padStart(2, "0")}`;
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(r);
    });
    return [...out.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="schedules-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Live Sailing Schedules</h1>
          <p className="text-sm text-zinc-500 mt-1">Plan around real vessel departures. Filter by lane and date window, compare cut-offs and transit times across carriers.</p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-[#16A34A] text-white">Live</span>
      </div>

      <div className="bg-white border border-zinc-200 p-5">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="data-label">Port of Loading</Label>
            <Combobox testId="sch-pol" value={origin} onChange={setOrigin} options={pols} placeholder="Any POL..." emptyText="No matching ports." />
          </div>
          <div>
            <Label className="data-label">Port of Discharge</Label>
            <Combobox testId="sch-pod" value={destination} onChange={setDestination} options={pods} placeholder={origin ? "Any POD..." : "Pick POL first"} disabled={!origin} />
          </div>
          <div>
            <Label className="data-label">ETD From</Label>
            <DatePicker testId="sch-from" value={from} onChange={setFrom} placeholder="any date" />
          </div>
          <div>
            <Label className="data-label">ETD To</Label>
            <DatePicker testId="sch-to" value={to} onChange={setTo} placeholder="any date" />
          </div>
          <Button data-testid="sch-search" onClick={run} disabled={busy || !origin} className="rounded-none bg-[#002FA7] hover:bg-[#002480] h-10">
            <Search className="w-4 h-4 mr-2" />{busy ? "..." : "Show schedules"}
          </Button>
          <Button data-testid="sch-reset" onClick={reset} variant="outline" className="rounded-none h-10 border-zinc-300 hover:border-[#991B1B] hover:text-[#991B1B]">Reset</Button>
        </div>
      </div>

      {!hasSearched && !busy && (
        <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="sch-empty">
          <CalendarDays className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Pick a port of loading and click <span className="font-semibold text-zinc-700">Show schedules</span> to see upcoming sailings.</p>
        </div>
      )}

      {hasSearched && rows.length === 0 && !busy && (
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <AlertCircle className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-700 font-medium">No sailings found.</p>
          <p className="text-xs text-zinc-500 mt-1">Try a wider date range or a different lane.</p>
        </div>
      )}

      {weeks.map(([wk, items]) => (
        <div key={wk} className="bg-white border border-zinc-200" data-testid={`sch-week-${wk}`}>
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between bg-zinc-50">
            <div className="data-label flex items-center gap-2"><CalendarDays className="w-3 h-3 text-[#002FA7]" />{wk} · {items.length} sailing{items.length === 1 ? "" : "s"}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-zinc-100">
              <tr className="text-left">
                {["Carrier", "Vessel · Voyage", "Service", "Route", "Cut-off (SI)", "ETD", "ETA", "Transit", "Space"].map((h) => <th key={h} className="px-3 py-2 data-label">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const color = CARRIER_COLORS[s.carrier] || "#71717A";
                const tight = s.space === "TIGHT";
                return (
                  <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`sch-row-${s.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-6" style={{ background: color }} />
                        <span className="font-heading font-semibold">{s.carrier}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="metric text-xs font-medium">{s.vessel}</div>
                      <div className="metric text-[10px] text-zinc-400">{s.voyage}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs">{s.service}</td>
                    <td className="px-3 py-2.5 text-xs">
                      <div className="flex items-center gap-1.5"><Anchor className="w-3 h-3 text-zinc-400" /><span className="metric">{s.pol}</span><span className="text-zinc-400">→</span><span className="metric">{s.pod}</span></div>
                      <div className="text-[10px] text-zinc-400 mt-0.5">{s.transhipment}{s.ts_port ? ` · ${s.ts_port}` : ""}</div>
                    </td>
                    <td className="px-3 py-2.5 metric text-xs text-[#B45309]"><Clock className="w-3 h-3 inline mr-1" />{shortDate(s.cut_off)}</td>
                    <td className="px-3 py-2.5 metric text-xs font-medium">{shortDate(s.etd)}</td>
                    <td className="px-3 py-2.5 metric text-xs">{shortDate(s.eta)}</td>
                    <td className="px-3 py-2.5 metric text-xs">{s.transit_days}d</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 border ${tight ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" : "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"}`}>{s.space}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}
